/* Lid-O theme — interactions, scroll choreography, Ajax cart, Theme Editor mount/unmount.
   Each section carries data-lido="<module>"; modules register a mount(el) that returns an unmount(). */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const fine = matchMedia('(pointer: fine)').matches;
const cfg = window.lidoConfig || {};
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const T = cfg.strings || {};
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* ---------------------------------------------------------- money (shop.money_format from Liquid) */
export function formatMoney(cents, format = cfg.moneyFormat || '${{amount}}') {
  const n = (+cents || 0) / 100;
  const fmt = (th, dp, nd = 2) => { const [i, d] = n.toFixed(nd).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (nd ? dp + d : ''); };
  const key = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
  const v = {
    amount: () => fmt(',', '.'),
    amount_no_decimals: () => fmt(',', '.', 0),
    amount_with_comma_separator: () => fmt('.', ','),
    amount_no_decimals_with_comma_separator: () => fmt('.', ',', 0),
    amount_with_apostrophe_separator: () => fmt("'", '.'),
    amount_no_decimals_with_space_separator: () => fmt(' ', ',', 0),
    amount_with_space_separator: () => fmt(' ', ','),
    amount_with_period_and_space_separator: () => fmt(' ', '.'),
  }[key] || (() => fmt(',', '.'));
  return format.replace(/\{\{\s*\w+\s*\}\}/, v()).replace(/<[^>]*>/g, '');
}

/* ---------------------------------------------------------- shared scroll loop */
const scrollFns = new Set();
let ticking = false;
function runScroll() { ticking = false; const y = scrollY, vh = innerHeight; scrollFns.forEach(f => f(y, vh)); }
addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(runScroll); } }, { passive: true });
addEventListener('resize', runScroll);

/* ---------------------------------------------------------- reveal on scroll */
const rio = new IntersectionObserver(es => es.forEach(e => {
  if (!e.isIntersecting) return;
  const el = e.target, sib = el.parentElement ? $$(':scope > [data-reveal]', el.parentElement).indexOf(el) : 0;
  el.style.setProperty('--rd', Math.max(0, sib) * 90 + 'ms');
  el.classList.add('is-in'); rio.unobserve(el);
}), { rootMargin: '0px 0px -10% 0px' });
function observeReveals(r = document) { $$('[data-reveal]', r).forEach(el => rio.observe(el)); }

/* ---------------------------------------------------------- focus trap (drawers) */
function trapFocus(container, onClose) {
  const sel = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const prev = document.activeElement;
  const first = () => $$(sel, container).filter(el => el.offsetParent !== null);
  const key = e => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const f = first(); if (!f.length) return;
    const a = f[0], b = f[f.length - 1];
    if (e.shiftKey && document.activeElement === a) { e.preventDefault(); b.focus(); }
    else if (!e.shiftKey && document.activeElement === b) { e.preventDefault(); a.focus(); }
  };
  document.addEventListener('keydown', key);
  requestAnimationFrame(() => { const f = first(); (f[0] || container).focus({ preventScroll: true }); });
  return () => { document.removeEventListener('keydown', key); prev && prev.focus && prev.focus({ preventScroll: true }); };
}

/* ---------------------------------------------------------- module registry */
const registry = {};
const mounted = new WeakMap();
function mountAll(r = document) {
  const els = $$('[data-lido]', r);
  if (r !== document && r.matches && r.matches('[data-lido]')) els.unshift(r);
  els.forEach(el => {
    if (mounted.has(el)) return;
    const fn = registry[el.dataset.lido]; if (!fn) return;
    try { mounted.set(el, fn(el) || (() => {})); } catch (err) { console.error('Lid-O module', el.dataset.lido, err); }
  });
}
function unmountAll(r) {
  const els = $$('[data-lido]', r);
  if (r.matches && r.matches('[data-lido]')) els.unshift(r);
  els.forEach(el => { const un = mounted.get(el); if (un) { try { un(); } catch (e) { /* noop */ } mounted.delete(el); } });
}
document.addEventListener('shopify:section:load', e => { mountAll(e.target); observeReveals(e.target); navTheme(); runScroll(); });
document.addEventListener('shopify:section:unload', e => unmountAll(e.target));
document.addEventListener('shopify:section:reorder', () => navTheme());

/* ---------------------------------------------------------- nav: light/dark from the section under it (IntersectionObserver band) */
let navIO = null;
function navTheme() {
  const nav = $('[data-nav]'); if (!nav) return;
  navIO && navIO.disconnect();
  const h = nav.offsetHeight || 52, lit = new Set();
  navIO = new IntersectionObserver(es => {
    es.forEach(e => e.isIntersecting ? lit.add(e.target) : lit.delete(e.target));
    nav.classList.toggle('is-light', lit.size > 0);
  }, { rootMargin: `-${h - 1}px 0px -${Math.max(0, innerHeight - h - 1)}px 0px`, threshold: 0 });
  $$('[data-theme="light"]').forEach(s => navIO.observe(s));
}
let navResize = 0;
addEventListener('resize', () => { clearTimeout(navResize); navResize = setTimeout(navTheme, 150); });

/* ---------------------------------------------------------- header: mobile drawer */
registry.header = el => {
  const open = $('[data-menu-open]', el), menu = $('[data-menu]', el);
  if (!open || !menu) return;
  let release = null;
  const close = () => {
    menu.classList.remove('is-open'); open.setAttribute('aria-expanded', 'false'); document.body.classList.remove('has-drawer');
    release && release(); release = null;
    setTimeout(() => { menu.hidden = true; }, 500);
  };
  const show = () => {
    menu.hidden = false; open.setAttribute('aria-expanded', 'true'); document.body.classList.add('has-drawer');
    requestAnimationFrame(() => requestAnimationFrame(() => menu.classList.add('is-open')));
    release = trapFocus($('.menu__panel', menu), close);
  };
  open.addEventListener('click', show);
  $$('[data-menu-close]', menu).forEach(b => b.addEventListener('click', e => { if (b.tagName === 'A' && b.getAttribute('href')?.startsWith('#')) return close(); close(); }));
  navTheme();
  return () => { navIO && navIO.disconnect(); };
};

/* ---------------------------------------------------------- hero: entrance + parallax */
registry.hero = el => {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    document.body.classList.add('is-loaded');
    setTimeout(() => document.body.classList.add('is-settled'), 2600);
  }));
  const stage = $('[data-hero-stage]', el);
  const fn = (y, vh) => {
    if (!stage || reduce) return;
    const p = clamp(y / (vh * 1.1));
    stage.style.setProperty('--hy', (p * -80).toFixed(1) + 'px');
    stage.style.setProperty('--hs', (1 + p * 0.08).toFixed(3));
  };
  scrollFns.add(fn); fn(scrollY, innerHeight);
  return () => scrollFns.delete(fn);
};

/* ---------------------------------------------------------- ticker: pause control */
registry.ticker = el => {
  const btn = $('[data-ticker-pause]', el); if (!btn) return;
  const on = () => { const p = el.classList.toggle('is-paused'); btn.setAttribute('aria-pressed', p); };
  btn.addEventListener('click', on);
  if (reduce) { el.classList.add('is-paused'); btn.setAttribute('aria-pressed', 'true'); }
  return () => btn.removeEventListener('click', on);
};

/* ---------------------------------------------------------- problem: word-by-word light-up */
registry.problem = el => {
  const p = $('[data-words]', el); if (!p) return;
  const list = [];
  (function split(n) {
    [...n.childNodes].forEach(c => {
      if (c.nodeType === 1) return split(c);
      if (c.nodeType !== 3) return;
      const f = document.createDocumentFragment();
      c.textContent.split(/(\s+)/).forEach(t => {
        if (!t) return;
        if (/^\s+$/.test(t)) return f.appendChild(document.createTextNode(t));
        const s = document.createElement('span'); s.className = 'w'; s.textContent = t; list.push(s); f.appendChild(s);
      });
      c.replaceWith(f);
    });
  })(p);
  const fn = (y, vh) => {
    const r = p.getBoundingClientRect();
    const k = clamp((vh * 0.85 - r.top) / (vh * 0.45 + r.height * 0.6));
    const n = reduce ? list.length : Math.round(k * list.length);
    list.forEach((w, i) => w.classList.toggle('is-lit', i < n));
  };
  scrollFns.add(fn); fn(scrollY, innerHeight);
  return () => scrollFns.delete(fn);
};

/* ---------------------------------------------------------- film: scale on scroll, autoplay in view, pause button */
registry.film = el => {
  const video = $('video', el), btn = $('[data-film-pause]', el);
  let userPaused = reduce, vio = null;
  const play = () => { if (!userPaused) video.play().catch(() => {}); };
  if (video) {
    video.muted = true;
    vio = new IntersectionObserver(e => { if (e[0].isIntersecting) play(); else video.pause(); }, { threshold: 0.2 });
    vio.observe(video);
  }
  const toggle = () => { userPaused = !userPaused; btn.setAttribute('aria-pressed', userPaused); userPaused ? video.pause() : play(); };
  if (btn && video) { btn.addEventListener('click', toggle); btn.setAttribute('aria-pressed', userPaused); }
  const fn = (y, vh) => {
    const r = el.getBoundingClientRect();
    const p = clamp(-r.top / (r.height - vh) * 1.6);
    el.style.setProperty('--fp', reduce ? 1 : (1 - Math.pow(1 - p, 3)).toFixed(3));
  };
  scrollFns.add(fn); fn(scrollY, innerHeight);
  return () => { scrollFns.delete(fn); vio && vio.disconnect(); btn && btn.removeEventListener('click', toggle); };
};

/* ---------------------------------------------------------- how: 3D story, lazy-loaded one viewport before it is needed */
function webglOK() { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; } }
registry.how = el => {
  const canvas = $('[data-lid3d]', el);
  const steps = $$('[data-step]', el), dots = $$('[data-dot]', el);
  const callouts = Object.fromEntries($$('[data-callout]', el).map(c => [c.dataset.callout, c]));
  const enabled = el.dataset.enable3d !== 'false' && canvas && webglOK() && !reduce && el.dataset.model && el.dataset.three;
  let lid = null, loadIO = null, dead = false;
  if (!enabled) el.classList.add('no-webgl');
  /* sizes are measured once (and on resize), never inside the frame: reading layout right after writing styles forced a full
     page layout every frame — the main cause of the stutter on iPhone */
  let r = null;
  const measure = () => { r = canvas.getBoundingClientRect(); Object.values(callouts).forEach(c => { c._w = c.offsetWidth || 150; c._h = c.offsetHeight || 30; }); };
  const ro = new ResizeObserver(() => { r = null; }); ro.observe(canvas);
  document.fonts && document.fonts.ready.then(() => { r = null; });
  const onFrame = (st, project) => {
    if (!r) measure();
    const keys = Object.keys(callouts);
    keys.forEach((k, i) => {
      const c = callouts[k];
      const a = clamp((st.ex - 0.35 - i * 0.07) / 0.3), e = a * a * (3 - 2 * a);
      c._e = e; c.style.opacity = e.toFixed(3);
      c.style.setProperty('--s', (0.92 + e * 0.08).toFixed(3));
      c.style.setProperty('--oy', ((1 - e) * 10).toFixed(1) + 'px');
      if (e <= 0) return;
      const p = project(k), cw = c._w;
      if (c.dataset.side === 'above') {
        c.style.setProperty('--x', Math.min(r.width - cw - 12, Math.max(12, p.x * r.width - cw * 0.7)) + 'px');
        c.style.setProperty('--y', (p.y * r.height - c._h - 14) + 'px'); c._y = null; return;
      }
      c.style.setProperty('--x', Math.min(r.width - cw - 12, Math.max(12, p.x * r.width + 18)) + 'px');
      c._y = p.y * r.height - 14;
    });
    /* no overlapping labels: stack them top → bottom with a minimum gap */
    let last = -1e9;
    keys.map(k => callouts[k]).filter(c => c._e > 0 && c._y != null).sort((a, b) => a._y - b._y).forEach(c => {
      const y = Math.max(c._y, last + c._h + 16); last = y; c.style.setProperty('--y', y + 'px');
    });
  };
  /* phones: the same story as a pre-rendered image sequence (tools/render-seq.mjs), scrubbed on a 2D canvas like Apple's
     product pages — no WebGL work on the device at all, so nothing can stutter */
  const useSeq = !!el.dataset.seq && matchMedia('(max-width: 47.99em), (pointer: coarse) and (max-aspect-ratio: 4/5)').matches;
  const seqPlayer = () => {
    const ctx = canvas.getContext('2d');
    /* memory: a decoded 1209×2418 frame is ~12 MB — all 360 would be 4 GB, far beyond what iOS Safari allows a tab, so it kept
       throwing frames away and re-decoding them on the main thread mid-scroll (the stutter). Now only the compressed files stay
       in memory (~9 MB) and a small window around the current frame is decoded off the main thread (createImageBitmap). */
    const AHEAD = 14, BEHIND = 6, KEEP = 24, DECODERS = 3;
    let meta = null, blobs = [], bmp = new Map(), decoding = new Set(), target = 0, cur = 0, shown = -2, raf = 0, on = true, fit = null, base = el.dataset.seq, dir = 1, centre = 0;
    const url = i => base.replace(/-000\.(\w+)/, (m, ext) => '-' + String(i).padStart(3, '0') + '.' + ext);
    const size = () => {
      const r = canvas.getBoundingClientRect(), d = Math.min(devicePixelRatio || 1, 3);
      canvas.width = Math.round(r.width * d); canvas.height = Math.round(r.height * d);
      const s = Math.max(canvas.width / meta.w, canvas.height / meta.h);          // object-fit: cover
      fit = { s, x: (canvas.width - meta.w * s) / 2, y: (canvas.height - meta.h * s) / 2 };
      ctx.imageSmoothingQuality = 'high';
      shown = -2;
    };
    const toFrame = p => {                                                       // frames are spaced by motion (meta.p)
      const P = meta.p; if (!P) return p * (meta.n - 1);
      let lo = 0, hi = P.length - 1; while (hi - lo > 1) { const m = (lo + hi) >> 1; if (P[m] <= p) lo = m; else hi = m; }
      return lo + clamp((p - P[lo]) / ((P[hi] - P[lo]) || 1));
    };
    const nearest = i => { for (let d = 0; d < meta.n; d++) { if (bmp.has(i - d)) return i - d; if (bmp.has(i + d)) return i + d; } return -1; };
    /* decode the frames the viewer is about to reach (more in the scroll direction), drop the ones left behind */
    const pump = () => {
      if (!meta || !on) return;
      const want = [];
      for (let k = 0; k <= AHEAD; k++) { want.push(centre + dir * k); if (k <= BEHIND && k) want.push(centre - dir * k); }
      for (const i of want) {
        if (decoding.size >= DECODERS) break;
        if (i < 0 || i >= meta.n || bmp.has(i) || decoding.has(i) || !blobs[i]) continue;
        decoding.add(i);
        createImageBitmap(blobs[i]).then(b => {
          decoding.delete(i);
          if (!on || Math.abs(i - centre) > KEEP) { b.close && b.close(); }
          else { bmp.set(i, b); if (Math.abs(i - toFrame(cur)) < 2 || shown < 0) { shown = -2; kick(); } }
          pump();
        }, () => { decoding.delete(i); pump(); });
      }
      for (const [i, b] of bmp) if (Math.abs(i - centre) > KEEP && i !== 0) { b.close && b.close(); bmp.delete(i); }
    };
    const blit = (im, a) => { ctx.globalAlpha = a; ctx.drawImage(im, fit.x, fit.y, meta.w * fit.s, meta.h * fit.s); };
    const draw = () => {
      const f = toFrame(cur), c = Math.round(f);
      if (c !== centre) { dir = c > centre ? 1 : -1; centre = c; pump(); }
      if (Math.abs(f - shown) < 0.004) return;
      let i0 = Math.floor(f), i1 = Math.min(meta.n - 1, i0 + 1), t = f - i0;
      if (!bmp.has(i0) || !bmp.has(i1)) { i0 = i1 = nearest(c); t = 0; if (i0 < 0) return; }
      shown = f;
      blit(bmp.get(i0), 1); if (t > 0.01 && i1 !== i0) blit(bmp.get(i1), t); ctx.globalAlpha = 1;   // cross-fade between neighbours
      if (!el.classList.contains('is-3d')) el.classList.add('is-3d');
      const a0 = meta.a[i0], a1 = meta.a[i1], L = (u, v) => u + (v - u) * t;
      onFrame({ ex: L(meta.ex[i0], meta.ex[i1]) }, k => a0[k] ? {
        x: (L(a0[k][0], a1[k][0]) * meta.w * fit.s + fit.x) / canvas.width,
        y: (L(a0[k][1], a1[k][1]) * meta.h * fit.s + fit.y) / canvas.height } : { x: -1, y: -1 });
    };
    let last = 0;
    const tick = t => {
      raf = 0; const dt = Math.min(50, t - (last || t)); last = t;
      const d = target - cur;
      cur = Math.abs(d) < 0.0002 ? target : cur + d * (1 - Math.exp(-dt / 60));   // same glide as the 3D version
      draw();
      if (on && (cur !== target || shown !== toFrame(cur))) raf = requestAnimationFrame(tick); else last = 0;
    };
    const kick = () => { if (!raf && meta && fit) raf = requestAnimationFrame(tick); };
    const getBlob = i => fetch(url(i)).then(r => r.ok ? r.blob() : null).catch(() => null);
    const start = async (metaUrl) => {
      meta = await fetch(metaUrl).then(r => r.json());
      const b0 = await getBlob(0);
      let first = null; try { first = b0 && await createImageBitmap(b0); } catch (e) { first = null; }
      if (!first) return false;                                                  // no AVIF support → caller falls back to WebP
      blobs[0] = b0; bmp.set(0, first); size(); kick();
      /* download coarse → fine: every 32nd, 16th … then the rest — scrubbable after a dozen files */
      const queue = []; for (const st of [32, 16, 8, 4, 2, 1]) for (let i = 0; i < meta.n; i += st) if (i && !queue.includes(i)) queue.push(i);
      const worker = async () => { while (queue.length && on) { const i = queue.shift(), b = await getBlob(i); if (b) { blobs[i] = b; if (Math.abs(i - centre) <= AHEAD) pump(); } } };
      await Promise.all(Array.from({ length: 6 }, worker));
      return true;
    };
    start(el.dataset.seqMeta).then(ok => {
      if (ok || !on || !el.dataset.seqFallback) return;
      base = el.dataset.seqFallback; blobs = []; bmp.clear(); return start(el.dataset.seqFallbackMeta);
    }).catch(err => { console.warn('Lid-O sequence', err); el.classList.add('no-webgl'); });
    const ro2 = new ResizeObserver(() => { if (meta && fit) { size(); kick(); } }); ro2.observe(canvas);
    return { setProgress(p) { target = p; kick(); }, dispose() { on = false; cancelAnimationFrame(raf); ro2.disconnect(); bmp.forEach(b => b.close && b.close()); bmp.clear(); blobs = []; } };
  };
  /* phones, first choice: the same 360 frames as ONE H.264 video (keyframe every 4 frames), scrubbed by currentTime.
     The phone's hardware video decoder shows any frame in a few ms. Measured on an iPhone 16 Pro (23.10): video = a new frame
     on every display frame, no dropped frames; the image sequence dropped to 45–50 fps. Falls back to the image sequence. */
  const videoPlayer = () => {
    let meta = null, v = null, url = null, target = 0, cur = 0, raf = 0, on = true, want = 0, have = -1, req = -1, busy = false, fallback = null, last = 0, dog = 0, stalls = 0;
    const toFrame = p => {
      const P = meta.p; if (!P) return p * (meta.n - 1);
      let lo = 0, hi = P.length - 1; while (hi - lo > 1) { const m = (lo + hi) >> 1; if (P[m] <= p) lo = m; else hi = m; }
      return lo + clamp((p - P[lo]) / ((P[hi] - P[lo]) || 1));
    };
    const callouts = i => {
      const r = canvas.getBoundingClientRect(), s = Math.max(r.width / meta.w, r.height / meta.h), ox = (r.width - meta.w * s) / 2, oy = (r.height - meta.h * s) / 2, a = meta.a[i];
      onFrame({ ex: meta.ex[i] }, k => a[k] ? { x: (a[k][0] * meta.w * s + ox) / r.width, y: (a[k][1] * meta.h * s + oy) / r.height } : { x: -1, y: -1 });
    };
    const seek = () => {
      if (busy || want === have || !v) return;
      busy = true; req = want; v.currentTime = (req + 0.5) / meta.fps;      // middle of the frame: never lands on its neighbour
      /* iOS sometimes leaves a seek hanging forever (seen on an iPhone 16 Pro): wake the decoder with play/pause and seek again;
         if it keeps hanging, give up on video and use the image sequence */
      clearTimeout(dog);
      dog = setTimeout(() => {
        if (!busy || !v) return;
        if (++stalls > 3) return fail(new Error('seek stalled'));
        unlock().then(() => { busy = false; have = -1; seek(); });
      }, 400);
    };
    const unlock = () => v.play().then(() => v.pause(), () => {});
    const tick = t => {
      raf = 0; const dt = Math.min(50, t - (last || t)); last = t;
      const d = target - cur;
      cur = Math.abs(d) < 0.0002 ? target : cur + d * (1 - Math.exp(-dt / 60));
      want = Math.round(toFrame(cur)); seek();
      if (on && cur !== target) raf = requestAnimationFrame(tick); else last = 0;
    };
    const kick = () => { if (!raf && v && have >= 0) raf = requestAnimationFrame(tick); };
    const fail = err => {
      if (!on || fallback) return; console.warn('Lid-O video → image sequence', err || '');
      if (v) { v.remove(); v = null; } if (url) URL.revokeObjectURL(url);
      el.classList.remove('is-3d'); fallback = seqPlayer(); fallback.setProgress(target);
    };
    (async () => {
      const probe = document.createElement('video');
      if (!probe.canPlayType('video/mp4; codecs="avc1.640032"')) throw new Error('no H.264');
      meta = await fetch(el.dataset.videoMeta).then(r => r.json());
      /* the whole file (≈6 MB) in memory → every seek is local, no network round-trip mid-scroll */
      const blob = await fetch(el.dataset.video).then(r => { if (!r.ok) throw new Error('video ' + r.status); return r.blob(); });
      if (!on) return;
      url = URL.createObjectURL(blob);
      v = document.createElement('video');
      v.className = 'how__video'; v.muted = true; v.playsInline = true; v.preload = 'auto';
      v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('aria-hidden', 'true');
      v.addEventListener('seeked', () => {
        clearTimeout(dog); busy = false; have = req;
        if (!el.classList.contains('is-3d')) el.classList.add('is-3d');
        callouts(have);
        if (want !== have) seek();
      });
      v.addEventListener('error', () => fail(v && v.error));
      canvas.after(v);
      v.src = url;
      unlock();                                                                 // iOS only starts loading the file on play()…
      await new Promise((res, rej) => { if (v.readyState >= 2) res(); v.addEventListener('loadeddata', res, { once: true }); setTimeout(() => rej(new Error('video timeout')), 15000); });
      await unlock();                                                           // …and only decodes seeks after a play that was not cut short
      have = -1; want = Math.round(toFrame(cur = target)); seek(); kick();
    })().catch(fail);
    return {
      setProgress(p) { if (fallback) return fallback.setProgress(p); target = p; if (have >= 0) kick(); else cur = target; },
      dispose() { on = false; cancelAnimationFrame(raf); clearTimeout(dog); if (v) v.remove(); if (url) URL.revokeObjectURL(url); fallback && fallback.dispose(); },
    };
  };
  const load = () => {
    loadIO && loadIO.disconnect(); loadIO = null;
    if (useSeq) { lid = el.dataset.video ? videoPlayer() : seqPlayer(); fn(scrollY, innerHeight); return; }
    import(el.dataset.three).then(({ mountLid }) => {
      if (dead) return;
      lid = mountLid(canvas, { model: el.dataset.model, onFrame, onReady: () => requestAnimationFrame(() => el.classList.add('is-3d')) });
      fn(scrollY, innerHeight);
    }).catch(err => { console.warn('Lid-O 3D unavailable', err); el.classList.add('no-webgl'); });
  };
  if (enabled) {
    /* three.js (172 KB gz) + GLB (731 KB gz) only start downloading when the section is one viewport away */
    /* …and never before the page itself has finished loading: shopping comes first, the 3D story second */
    const start = () => {
      if (dead) return;
      loadIO = new IntersectionObserver(e => { if (e[0].isIntersecting) load(); }, { rootMargin: useSeq ? '300% 0px 300% 0px' : '100% 0px 100% 0px' });
      loadIO.observe(el);
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 300));
    if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
  }
  const move = e => {
    if (!lid) return; const r = el.getBoundingClientRect();
    lid.setPointer((e.clientX / r.width) * 2 - 1, ((e.clientY - Math.max(0, r.top)) / innerHeight) * 2 - 1);
  };
  if (fine) el.addEventListener('pointermove', move);
  const bounds = [[0, 0.18], [0.18, 0.42], [0.42, 0.87], [0.87, 1]];
  const fn = (y, vh) => {
    const r = el.getBoundingClientRect();
    const p = clamp(-r.top / (r.height - vh));
    if (lid) lid.setProgress(p);
    const idx = p < 0.18 ? 0 : p < 0.42 ? 1 : p < 0.87 ? 2 : 3;
    steps.forEach((s, i) => s.classList.toggle('is-on', i === idx));
    dots.forEach((d, i) => { const b = bounds[i] || [0, 1]; d.style.setProperty('--f', clamp((p - b[0]) / (b[1] - b[0])).toFixed(3)); });
  };
  scrollFns.add(fn); fn(scrollY, innerHeight);
  return () => { dead = true; scrollFns.delete(fn); loadIO && loadIO.disconnect(); el.removeEventListener('pointermove', move); ro.disconnect(); lid && lid.dispose && lid.dispose(); lid = null; };
};

/* ---------------------------------------------------------- dose: size × full / half */
registry.dose = el => {
  const counter = $('[data-count]', el), h = $('[data-dose-h]', el), para = $('[data-dose-p]', el), cap = $('[data-dose-cap]', el);
  const stageEl = $('.dosefig__stage', el), lids = $$('[data-lid]', el), sizes = $$('[data-size]', el);
  if (!counter || !sizes.length) return;
  const data = Object.fromEntries(sizes.map(b => [b.dataset.size, { cc: +b.dataset.cc || 0, half: +b.dataset.half || (+b.dataset.cc / 2), mm: b.dataset.mm, ak: +b.dataset.scale || 1, label: b.dataset.label || '' }]));
  let size = el.dataset.default || sizes[0].dataset.size, dose = 'full', shown = 0, raf = 0, started = false;
  if (!data[size]) size = sizes[0].dataset.size;
  const fmt = v => (Math.round(v * 10) / 10).toString();
  const target = () => dose === 'half' ? data[size].half : data[size].cc;
  const brify = s => esc(s).replace(/\n/g, '<br>');
  const pre = new IntersectionObserver(e => { if (!e[0].isIntersecting) return; pre.disconnect(); $$('img', el).forEach(i => { i.loading = 'eager'; i.decode && i.decode().catch(() => {}); }); }, { rootMargin: '600px 0px' });
  pre.observe(el);
  function render(dur = 600) {
    const to = target(), from = shown, t0 = performance.now(); shown = to;
    cancelAnimationFrame(raf);
    (function tick(t) { const p = clamp((t - t0) / (reduce ? 1 : dur)); const v = from + (to - from) * (1 - Math.pow(1 - p, 4)); counter.textContent = p < 1 ? Math.round(v) : (dose === 'full' && data[size].label) || fmt(to); if (p < 1) raf = requestAnimationFrame(tick); })(t0);
    if (cap) cap.textContent = (cap.dataset.template || '[size] · [mm] mm').replace('[size]', size.toUpperCase()).replace('[mm]', data[size].mm || '') + (dose === 'half' ? ' ' + (cap.dataset.adapter || '') : '');
    stageEl && stageEl.style.setProperty('--ak', data[size].ak);
  }
  const cio = new IntersectionObserver(e => { if (!e[0].isIntersecting) return; cio.disconnect(); started = true; render(1600); }, { threshold: 0.6 });
  cio.observe(counter);
  sizes.forEach(b => b.addEventListener('click', () => {
    if (b.dataset.size === size) return; size = b.dataset.size;
    sizes.forEach(x => x.setAttribute('aria-pressed', x === b));
    lids.forEach(l => l.classList.toggle('is-on', l.dataset.lid === size));
    render();
  }));
  $$('[data-dose]', el).forEach(b => b.addEventListener('click', () => {
    if (b.dataset.dose === dose) return; dose = b.dataset.dose;
    $$('[data-dose]', el).forEach(x => x.setAttribute('aria-pressed', x === b));
    el.classList.toggle('is-half', dose === 'half');
    if (h) h.innerHTML = brify(h.dataset[dose] || '');
    if (para) para.textContent = para.dataset[dose] || '';
    render();
  }));
  render(1); counter.textContent = started ? fmt(target()) : '0'; shown = 0;
  return () => { cancelAnimationFrame(raf); cio.disconnect(); pre.disconnect(); };
};

/* ---------------------------------------------------------- before / after */
registry['before-after'] = el => {
  const f = $('[data-ba]', el), range = $('[data-ba-range]', el); if (!f || !range) return;
  const set = v => f.style.setProperty('--pos', v + '%');
  range.addEventListener('input', () => set(range.value));
  let bio = null;
  if (!reduce) {
    bio = new IntersectionObserver(e => {
      if (!e[0].isIntersecting) return; bio.disconnect();
      const t0 = performance.now();
      (function sweep(t) { const p = clamp((t - t0) / 1800); const v = 50 + Math.sin(p * Math.PI * 2) * 22 * (1 - p); set(v); range.value = v; if (p < 1) requestAnimationFrame(sweep); })(t0);
    }, { threshold: 0.6 });
    bio.observe(f);
  }
  return () => bio && bio.disconnect();
};

/* ---------------------------------------------------------- fit finder: brand search + measure, tablist with arrow keys */
registry.fit = el => {
  let FIT = [];
  try { FIT = JSON.parse($('[data-fit-data]', el)?.textContent || '[]'); } catch (e) { FIT = []; }
  const fitIn = $('[data-fit-input]', el), fitOut = $('[data-fit-results]', el);
  const badge = z => ({ L: fitOut.dataset.l, M: fitOut.dataset.m, S: fitOut.dataset.s, XS: fitOut.dataset.xs }[z] || z);
  function renderFit(q) {
    q = q.trim().toLowerCase();
    if (!q) { fitOut.innerHTML = ''; return; }
    const hits = FIT.filter(r => String(r[0]).toLowerCase().includes(q)).slice(0, 10);
    fitOut.innerHTML = hits.length
      ? hits.map(([b, t, s, z], i) => z === '?' || !z
        ? `<li style="--i:${i}"><strong>${esc(b)}</strong><button type="button" class="badge badge--q" data-go-measure>${esc(fitOut.dataset.measure)}</button><small>${esc(t)} · ${esc(s)}</small></li>`
        : `<li style="--i:${i}"><strong>${esc(b)}</strong><span class="badge badge--${String(z).toLowerCase()}">${esc(badge(z))}</span><small>${esc(t)} · ${esc(s)}</small></li>`).join('')
      : `<li class="empty">${esc(fitOut.dataset.empty)}</li>`;
  }
  if (fitIn && fitOut) fitIn.addEventListener('input', () => renderFit(fitIn.value));

  const seg = $('[data-tablist]', el), tabs = seg ? $$('[role=tab]', seg) : [];
  const select = i => {
    seg.dataset.i = i;
    tabs.forEach((x, j) => { x.setAttribute('aria-selected', i === j); x.tabIndex = i === j ? 0 : -1; const p = $('#' + x.getAttribute('aria-controls'), el); if (p) p.hidden = i !== j; });
  };
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => select(i));
    t.addEventListener('keydown', e => {
      const n = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
      if (n < 0 && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp') return;
      e.preventDefault(); const k = (n + tabs.length) % tabs.length; select(k); tabs[k].focus();
    });
  });
  if (fitOut) fitOut.addEventListener('click', e => { if (e.target.closest('[data-go-measure]')) { select(1); tabs[1] && tabs[1].focus(); } });

  const mm = $('[data-mm-range]', el), ring = $('[data-ring]', el), mmOut = $('[data-mm]', el), verdict = $('[data-verdict]', el);
  let SIZES = [];
  try { SIZES = JSON.parse(verdict?.dataset.sizes || '[]'); } catch (e) { SIZES = []; }
  const tol = +(verdict?.dataset.tolerance || 4);
  function measure() {
    const v = +mm.value; ring && ring.style.setProperty('--mm', v); mmOut && (mmOut.textContent = v);
    const hit = SIZES.find(s => Math.abs(v - s.mm) <= tol);
    let txt;
    if (!hit) txt = `${esc(verdict.dataset.between)} <a href="${esc(verdict.dataset.sendUrl)}">${esc(verdict.dataset.send)}</a>`;
    else if (hit.soon) txt = verdict.dataset.soon.replace('[size]', esc(hit.key)) + ` <a href="${esc(verdict.dataset.notifyUrl)}">${esc(verdict.dataset.notify)}</a>.`;
    else txt = verdict.dataset.fit.replace('[size]', esc(hit.key)).replace('[mm]', esc(hit.label || hit.mm + ' mm'));
    verdict.innerHTML = txt;
  }
  if (mm && verdict) { mm.addEventListener('input', measure); measure(); }
  return () => {};
};

/* ---------------------------------------------------------- cart drawer (Ajax Cart API + Section Rendering API) */
const cartApi = (() => {
  let drawer = null, release = null, busy = false;
  const body = () => drawer && $('[data-cart-drawer-body]', drawer);
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  function open() {
    if (!drawer) { location.href = cfg.cartUrl || root + 'cart'; return; }
    drawer.hidden = false; document.body.classList.add('has-drawer');
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
    release = trapFocus($('.cart__panel', drawer), close);
  }
  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open'); document.body.classList.remove('has-drawer');
    release && release(); release = null;
    setTimeout(() => { drawer.hidden = true; }, 550);
  }
  function applySections(sections) {
    if (!sections) return;
    const parse = html => new DOMParser().parseFromString(html, 'text/html');
    if (sections['cart-drawer'] && body()) {
      const fresh = parse(sections['cart-drawer']).querySelector('[data-cart-drawer-body]');
      if (fresh) body().innerHTML = fresh.innerHTML;
    }
    if (sections.header) {
      const c = parse(sections.header).querySelector('[data-cart-count]');
      if (c) $$('[data-cart-count]').forEach(x => { x.textContent = c.textContent.trim(); x.hidden = c.hidden; });
    }
  }
  async function refresh() {
    const res = await fetch(`${root}?sections=cart-drawer,header`, { headers: { Accept: 'application/json' } });
    if (res.ok) applySections(await res.json());
  }
  async function add(payload) {
    busy = true; drawer && drawer.classList.add('is-busy');
    try {
      const res = await fetch(cfg.cartAddUrl || root + 'cart/add.js', { method: 'POST', headers, body: JSON.stringify({ ...payload, sections: 'cart-drawer,header', sections_url: root }) });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || T.error || 'Error');
      applySections(json.sections);
      return json;
    } finally { busy = false; drawer && drawer.classList.remove('is-busy'); }
  }
  async function change(id, quantity) {
    busy = true; drawer && drawer.classList.add('is-busy');
    try {
      const res = await fetch(cfg.cartChangeUrl || root + 'cart/change.js', { method: 'POST', headers, body: JSON.stringify({ id, quantity, sections: 'cart-drawer,header', sections_url: root }) });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Error');
      applySections(json.sections);
      return json;
    } finally { busy = false; drawer && drawer.classList.remove('is-busy'); }
  }
  return { open, close, add, change, refresh, setDrawer(d) { drawer = d; }, get busy() { return busy; } };
})();
window.lidoCart = cartApi;

registry['cart-drawer'] = el => {
  cartApi.setDrawer(el);
  $$('[data-close-cart]', el).forEach(b => b.addEventListener('click', cartApi.close));
  const onClick = e => {
    const b = e.target.closest('[data-cart-qty]'); if (!b || cartApi.busy) return;
    const item = b.closest('[data-key]'); if (!item) return;
    cartApi.change(item.dataset.key, +b.dataset.cartQty).catch(err => console.warn(err));
  };
  el.addEventListener('click', onClick);
  return () => { el.removeEventListener('click', onClick); cartApi.setDrawer(null); };
};
document.addEventListener('click', e => {
  const b = e.target.closest('[data-open-cart]'); if (!b) return;
  e.preventDefault(); cartApi.open();
});

/* ---------------------------------------------------------- cart page: qty buttons */
registry['cart-page'] = el => {
  const onClick = e => {
    const b = e.target.closest('[data-cart-qty]'); if (!b) return;
    const row = b.closest('[data-line]'); if (!row) return;
    const input = $('[data-cart-qty-input]', row);
    const key = input && input.id.replace('updates_', '');
    if (!key) return;
    b.disabled = true;
    cartApi.change(key, +b.dataset.cartQty).then(() => location.reload()).catch(() => { b.disabled = false; });
  };
  el.addEventListener('click', onClick);
  return () => el.removeEventListener('click', onClick);
};

/* ---------------------------------------------------------- product / buy module */
registry.product = el => {
  const radios = $$('input[name=size]', el), priceEl = $('[data-price]', el), compareEl = $('[data-compare]', el);
  const main = $('[data-gallery-main]', el) || $('.buy__main img', el), thumbs = $('[data-thumbs]', el), qIn = $('[data-qty-input]', el);
  const form = $('[data-product-form]', el), idIn = $('[data-form-id]', el), addBtn = $('[data-add]', el), addLabel = $('[data-add-label]', el);
  const notify = $('[data-notify]', el), errEl = $('[data-form-error]', el);
  const titleEl = $('[data-buy-title]', el);
  const notifyText = notify && $('[data-notify-text]', notify), notifyTag = notify && $('[data-notify-tag]', notify);
  const notifyPosted = notify && $('[data-notify-posted]', notify);
  const maxQ = +(qIn?.max || 20);
  let gallerySrc = null;
  function showMain(src, btn) {
    if (!main) return;
    $$('[data-thumb]', el).forEach(x => x.classList.toggle('is-on', x === btn));
    if (!src || main.getAttribute('src') === src) return;
    main.classList.add('is-swap');
    const photo = btn ? btn.dataset.photo === '1' : false;
    setTimeout(() => { main.onload = () => main.classList.remove('is-swap'); main.parentNode.classList.toggle('is-photo', photo); main.removeAttribute('srcset'); main.removeAttribute('sizes'); main.src = src; }, 200);
  }
  /* each size is its own product → swap the whole gallery (main image + thumbs) to that product's media */
  function showGallery(r) {
    let media = [];
    try { media = JSON.parse(r.dataset.gallery || '[]'); } catch (e) { media = []; }
    if (!media.length) { if (r.dataset.image) showMain(r.dataset.image, null); return; }
    if (gallerySrc === r.dataset.gallery) return;
    gallerySrc = r.dataset.gallery;
    if (thumbs) {
      thumbs.innerHTML = media.map(([big, small, photo], i) => `<button type="button" class="${i ? '' : 'is-on'}" data-thumb="${esc(big)}" data-photo="${photo ? 1 : 0}" aria-label="${esc((T.viewImage || 'Image') + ' ' + (i + 1))}"><img src="${esc(small)}" alt="" loading="lazy" width="300" height="300"${photo ? ' class="is-photo"' : ''}></button>`).join('');
      thumbs.style.setProperty('--n', Math.min(media.length, 7));
      thumbs.hidden = media.length < 2;
    }
    showMain(media[0][0], thumbs ? $('[data-thumb]', thumbs) : null);
  }
  function select(r) {
    const soon = r.dataset.soon === 'true';
    const available = !soon && r.dataset.available !== 'false';
    if (notify) {
      notify.hidden = available && !notifyPosted;
      if (!available) {
        const key = r.dataset.sizeKey || 'XL';
        const later = soon || r.dataset.soonProduct === 'true';
        /* theme-owned locale text, already HTML-escaped by the t filter */
        notifyText && (notifyText.innerHTML = later ? notify.dataset.soonText : notify.dataset.restockText);
        notifyTag && (notifyTag.value = 'notify-' + key.toLowerCase());
      }
    }
    if (soon) { addBtn && (addBtn.disabled = true); addLabel && (addLabel.textContent = T.unavailable || 'Unavailable'); return; }
    idIn && (idIn.value = r.dataset.variantId || r.value);
    if (priceEl) priceEl.textContent = formatMoney(r.dataset.price);
    if (compareEl) {
      const show = compareEl.dataset.enabled === 'true' && +r.dataset.compare > +r.dataset.price;
      compareEl.hidden = !show; compareEl.textContent = show ? formatMoney(r.dataset.compare) : '';
    }
    if (addBtn) {
      addBtn.disabled = !available;
      addLabel && (addLabel.textContent = available ? (T.addToBag || 'Add to bag') : (r.dataset.soonProduct === 'true' ? (T.comingSoon || T.unavailable || 'Coming soon') : (T.soldOut || 'Sold out')));
    }
    showGallery(r);
    if (titleEl && r.dataset.title) {
      /* same as snippets/title-nobreak: "LID-O Large" → LID-O / LARGE, never split at the hyphen */
      const m = r.dataset.title.match(/^(lid-o)\s+(.+)$/i);
      titleEl.innerHTML = m ? `<span class="nw">${esc(m[1])}</span> <span class="buy__size">${esc(m[2])}</span>` : esc(r.dataset.title);
    }
  }
  radios.forEach(r => r.addEventListener('change', () => select(r)));
  const checked = radios.find(r => r.checked); checked && select(checked);
  const unFinder = finder(el, radios);
  $$('[data-qty]', el).forEach(b => b.addEventListener('click', () => { if (qIn) qIn.value = clamp(+qIn.value + +b.dataset.qty, 1, maxQ); }));
  thumbs && thumbs.addEventListener('click', e => { const b = e.target.closest('[data-thumb]'); if (b) showMain(b.dataset.thumb, b); });
  const submit = async e => {
    e.preventDefault();
    if (!idIn || !addBtn || addBtn.disabled || cartApi.busy) return;
    errEl && (errEl.hidden = true);
    addBtn.disabled = true; const prev = addLabel ? addLabel.textContent : ''; addLabel && (addLabel.textContent = T.adding || '…');
    try {
      await cartApi.add({ items: [{ id: +idIn.value, quantity: Math.max(1, +(qIn?.value || 1)) }] });
      cartApi.open();
    } catch (err) {
      if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
    } finally { addBtn.disabled = false; addLabel && (addLabel.textContent = prev); }
  };
  form && form.addEventListener('submit', submit);
  const sbuy = $('[data-sticky-buy]', el);
  let io = null, onScroll = null;
  if (sbuy) {
    let past = false, inView = false;
    const upd = () => sbuy.classList.toggle('is-on', past && !inView);
    io = new IntersectionObserver(es => { es.forEach(e => { inView = e.isIntersecting; }); upd(); }, { threshold: 0.12 });
    io.observe(el);
    onScroll = () => { past = scrollY > innerHeight * 0.8; upd(); };
    addEventListener('scroll', onScroll, { passive: true }); onScroll();
    sbuy.addEventListener('click', e => { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth' }); });
  }
  return () => { form && form.removeEventListener('submit', submit); unFinder(); io && io.disconnect(); onScroll && removeEventListener('scroll', onScroll); };
};

/* ---------------------------------------------------------- fit finder inside the buy module: type or scroll your brand → size selected */
function finder(el, radios) {
  const box = $('[data-finder]', el); if (!box) return () => {};
  let FIT = [];
  try { FIT = JSON.parse($('[data-fit-data]', box)?.textContent || '[]'); } catch (e) { FIT = []; }
  const combo = $('[data-combo]', box), input = $('[data-combo-input]', box), list = $('[data-combo-list]', box), verdict = $('[data-verdict]', box);
  const measureBox = $('[data-measure]', box), measureBtn = $('[data-measure-toggle]', box);
  const mm = $('[data-mm-range]', box), ring = $('[data-ring]', box), mmOut = $('[data-mm]', box);
  const D = box.dataset, fill = (t, o) => Object.entries(o).reduce((a, [k, v]) => a.split(`[${k}]`).join(esc(v)), t || '');
  /* variant option "L", "Large", "Large (5\")" … → L */
  const keyOf = t => { t = String(t || '').trim().toUpperCase(); if (/^(XL|EXTRA ?LARGE)/.test(t)) return 'XL'; if (/^(XS|EXTRA ?SMALL)/.test(t)) return 'XS'; return /^[SML]/.test(t) ? t[0] : t; };
  const radioFor = k => radios.find(r => r.dataset.product === 'main' && keyOf(r.dataset.sizeKey) === k);
  const pick = k => { const r = radioFor(k); if (!r) return null; r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); return r; };
  let shown = [], active = -1;
  function fillList(q) {
    q = q.trim().toLowerCase();
    shown = FIT.filter(([b]) => !q || b.toLowerCase().includes(q)).sort((a, b) => a[0].localeCompare(b[0]));
    active = -1;
    list.innerHTML = shown.length
      ? shown.map(([b, t, j, z], k) => `<li role="option" id="${list.id}-${k}" data-k="${k}" aria-selected="false"><span><strong>${esc(b)}</strong><small>${esc(t)} · ${esc(j)}</small></span><em class="${z === 'L' ? 'is-l' : z === '?' ? 'is-q' : ''}">${z === '?' ? esc(D.measureLabel) : esc(z)}</em></li>`).join('')
      : `<li class="combo__empty"><button type="button" data-measure-open>${esc(D.empty)}</button></li>`;
  }
  function openList(on) { list.hidden = !on; input.setAttribute('aria-expanded', on); combo.classList.toggle('is-open', on); if (!on) input.removeAttribute('aria-activedescendant'); }
  function setActive(k) {
    const o = $$('[role=option]', list); if (!o.length) return;
    active = (k + o.length) % o.length; o.forEach((x, i) => x.setAttribute('aria-selected', i === active));
    input.setAttribute('aria-activedescendant', o[active].id); o[active].scrollIntoView({ block: 'nearest' });
  }
  function showMeasure(on) { measureBox.hidden = !on; measureBtn.setAttribute('aria-expanded', on); measureBtn.textContent = on ? D.hide : D.show; }
  function choose(k) {
    const [b, t, j, z] = shown[k]; input.value = b; openList(false);
    if (z === '?') { verdict.innerHTML = fill(D.depends, { brand: b }); showMeasure(true); return; }
    const r = pick(z);
    verdict.innerHTML = fill(r && r.dataset.available === 'false' ? D.fitSold : D.fit, { size: z, brand: b, jar: `${t}, ${j}` });
  }
  function measure() {
    const v = +mm.value; ring.style.setProperty('--mm', v); mmOut.textContent = v;
    const hit = [[95, 'XS', '95 mm'], [107, 'S', '107 mm'], [117, 'M', '117 mm · 4.5″'], [127, 'L', '127 mm · 5″'], [142, 'XL', '142 mm']].find(([d]) => Math.abs(v - d) <= 4);
    if (!hit) { verdict.innerHTML = esc(D.between); return; }
    const r = radioFor(hit[1]);
    if (!r) { verdict.innerHTML = fill(D.soon, { size: hit[1] }); return; }
    if (r.dataset.available === 'false') { verdict.innerHTML = fill(D.sold, { size: hit[1], mm: hit[2] }); return; }
    pick(hit[1]); verdict.innerHTML = fill(D.isFit, { size: hit[1], mm: hit[2] });
  }
  const onFocus = () => { fillList(''); openList(true); };
  const onInput = () => { fillList(input.value); openList(true); };
  const onKey = e => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (list.hidden) { fillList(input.value); openList(true); } setActive(active + (e.key === 'ArrowDown' ? 1 : -1)); }
    else if (e.key === 'Enter' && !list.hidden && active > -1) { e.preventDefault(); choose(active); }
    else if (e.key === 'Escape') openList(false);
  };
  const onToggle = () => { if (list.hidden) { fillList(''); openList(true); input.focus({ preventScroll: true }); } else openList(false); };
  const keep = e => e.preventDefault();
  const onList = e => { const o = e.target.closest('[role=option]'); if (o) return choose(+o.dataset.k); if (e.target.closest('[data-measure-open]')) { openList(false); showMeasure(true); } };
  const outside = e => { if (!combo.contains(e.target)) openList(false); };
  const onMeasureBtn = () => showMeasure(measureBox.hidden);
  const toggleBtn = $('[data-combo-toggle]', box);
  input.addEventListener('focus', onFocus); input.addEventListener('input', onInput); input.addEventListener('keydown', onKey);
  toggleBtn.addEventListener('click', onToggle); list.addEventListener('pointerdown', keep); list.addEventListener('click', onList);
  document.addEventListener('pointerdown', outside); measureBtn.addEventListener('click', onMeasureBtn); mm.addEventListener('input', measure);
  return () => { document.removeEventListener('pointerdown', outside); };
}

/* ---------------------------------------------------------- instagram reels: endless slide, videos play only while on screen */
registry.instagram = el => {
  const track = $('[data-ig-track]', el), pauseBtn = $('[data-ig-pause]', el);
  if (!track) return;
  if (!reduce) {                                      // second copy of the cards → seamless loop (copies hidden from AT/keyboard)
    $$('.ig__card', track).forEach(c => { const d = c.cloneNode(true); d.setAttribute('aria-hidden', 'true'); d.tabIndex = -1; d.removeAttribute('data-shopify-editor-block'); track.appendChild(d); });
    track.style.setProperty('--dur', ($$('.ig__card', track).length * 4.2) + 's');
  }
  /* posters (9 × ~35 KB) only once the rail is near — they used to load with the first screen */
  const pio = new IntersectionObserver(e => {
    if (!e[0].isIntersecting) return; pio.disconnect();
    $$('video[data-poster]', track).forEach(v => { v.poster = v.dataset.poster; });
  }, { rootMargin: '800px 0px' });
  pio.observe(track);
  let paused = false;
  const vio = new IntersectionObserver(es => es.forEach(e => {
    const v = e.target;
    if (e.isIntersecting && !paused && !reduce) { v.preload = 'auto'; v.play().catch(() => {}); } else v.pause();
  }), { rootMargin: '0px 120px' });
  const vids = $$('video', track); vids.forEach(v => { v.muted = true; vio.observe(v); });
  if (reduce) $$('.ig__card', track).forEach(c => { const v = $('video', c); if (!v) return; const on = () => v.play().catch(() => {}), off = () => v.pause(); c.addEventListener('pointerenter', on); c.addEventListener('pointerleave', off); });
  const onPause = () => {
    paused = !paused; el.classList.toggle('is-paused', paused);
    pauseBtn.setAttribute('aria-pressed', paused); pauseBtn.textContent = paused ? pauseBtn.dataset.play : pauseBtn.dataset.pause;
    vids.forEach(v => { if (paused) v.pause(); else { vio.unobserve(v); vio.observe(v); } });
  };
  pauseBtn && pauseBtn.addEventListener('click', onPause);
  return () => { pio.disconnect(); vio.disconnect(); pauseBtn && pauseBtn.removeEventListener('click', onPause); };
};

/* ---------------------------------------------------------- quick-shop hero: film plays only while visible, CTA lands on the brand finder */
registry['quick-hero'] = el => {
  const video = $('[data-qh-video]', el), btn = $('[data-qh-pause]', el), cta = $('[data-qh-cta]', el);
  let userPaused = reduce, vio = null;
  const play = () => { if (!userPaused) video.play().catch(() => {}); };
  if (video) {
    video.muted = true;
    if (reduce) video.pause();
    vio = new IntersectionObserver(e => { if (e[0].isIntersecting) play(); else video.pause(); }, { threshold: 0.15 });
    vio.observe(video);
  }
  const toggle = () => { userPaused = !userPaused; btn.setAttribute('aria-pressed', userPaused); el.classList.toggle('is-paused', userPaused); userPaused ? video.pause() : play(); };
  if (btn && video) { btn.addEventListener('click', toggle); btn.setAttribute('aria-pressed', userPaused); el.classList.toggle('is-paused', userPaused); }
  const go = e => {
    const buy = $('#buy'); if (!buy) return;
    e.preventDefault();
    const input = $('[data-combo-input]', buy), finderBox = $('[data-finder]', buy), price = $('[data-buy-title]', buy);
    /* land on price → "What's your protein brand?" → sizes → Add to bag, not on the product photo */
    const target = !fine && (price || finderBox) ? (price || finderBox).getBoundingClientRect().top + scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav')) || 52) - 20 : buy.getBoundingClientRect().top + scrollY;
    scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
    /* desktop: cursor straight into "What's your protein brand?"; touch: no surprise keyboard, just point at it */
    setTimeout(() => {
      if (fine && input) input.focus({ preventScroll: true });
      else if (finderBox) { finderBox.classList.remove('is-hint'); void finderBox.offsetWidth; finderBox.classList.add('is-hint'); }
    }, reduce ? 0 : 650);
  };
  cta && cta.addEventListener('click', go);
  return () => { vio && vio.disconnect(); btn && btn.removeEventListener('click', toggle); cta && cta.removeEventListener('click', go); };
};

/* ---------------------------------------------------------- boot */
observeReveals();
mountAll();
navTheme();
runScroll();
if (!$('[data-lido="hero"]')) document.body.classList.add('is-loaded', 'is-settled');

/* ---------------------------------------------------------- geo: first visit shows the visitor's own market (EUR in the EU, £ in the UK …).
   Shopify only *detects* the country; without this every visitor stays in the default market (USD). Runs once per browser. */
(async () => {
  try {
    if (localStorage.getItem('lido-geo')) return;
    localStorage.setItem('lido-geo', '1');
    const current = window.Shopify && Shopify.country;
    const res = await fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } });
    const code = (await res.json())?.detected_values?.country?.handle;
    if (!code || !current || code === current) return;
    const f = document.createElement('form');
    f.method = 'post'; f.action = '/localization'; f.hidden = true;
    f.innerHTML = `<input type="hidden" name="form_type" value="localization"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="_method" value="put"><input type="hidden" name="country_code" value="${esc(code)}"><input type="hidden" name="return_to" value="${esc(location.pathname + location.search + location.hash)}">`;
    document.body.appendChild(f); f.submit();
  } catch (e) { /* storage blocked or offline: stay in the default market */ }
})();
